import React, { useState, useEffect, useRef, useMemo } from "react";
import { SquadflixChatApi, ChatMessage, ChatRoomDetails } from "../../../apis/squadflix-chat/SquadflixChatApi";
import { SquadflixFriendsApi, UserSummary } from "../../../apis/squadflix-friends/SquadflixFriendsApi";
import { useSocketEvents } from "../squadflix-tv-mode/socket-utils/socketUtils";
import { Spinner } from "../../shared";
import auth from "../../../utils/authentication";
import avatarDefault from "../../../images/avatardefault.png";
import { getCachedImageUrl } from "../../../utils/utils";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { SquadDiskUsersApi } from "../../../apis/squaddisk-users/SquadDiskUsersApi";
import { getTmdbImage } from "../../../utils/consts";
import { searchPage } from "../../cap17drive/api/cap17drive-api";
import { useSelector } from "react-redux";
import users from "../../users";
import "./SquadflixChatWidget.scss";

const SquadflixChatWidget: React.FC = () => {
    const darkMode = useSelector(users.selectors.isDarkMode);
    const navigate = useNavigate();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [view, setView] = useState<'list' | 'chat' | 'create-private' | 'create-group' | 'add-member' | 'edit-group' | 'user-profile'>('list');
    const [selectedProfileUser, setSelectedProfileUser] = useState<UserSummary | null>(null);
    const [detailedUserProfile, setDetailedUserProfile] = useState<any | null>(null);
    const [activeRoomId, setActiveRoomId] = useState<string | null>(null);
    const [chats, setChats] = useState<ChatRoomDetails[]>([]);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [friends, setFriends] = useState<UserSummary[]>([]);
    
    // Sockets state
    const [onlineFriendsUids, setOnlineFriendsUids] = useState<string[]>([]);
    
    // UI states
    const [currentTab, setCurrentTab] = useState<'all' | 'private' | 'group'>('all');
    const [inputText, setInputText] = useState("");
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
    const [attachmentUrls, setAttachmentUrls] = useState<Record<string, string>>({});
    const [groupPhotoUrls, setGroupPhotoUrls] = useState<Record<string, string>>({});
    
    // Lightbox attachment previews
    const [previewAttachmentUrl, setPreviewAttachmentUrl] = useState<string | null>(null);
    const [previewAttachmentName, setPreviewAttachmentName] = useState<string>("");
    const [previewAttachmentId, setPreviewAttachmentId] = useState<string | null>(null);
    const [previewAttachmentMime, setPreviewAttachmentMime] = useState<string>("");

    // Mention autocomplete states
    const [mentionSearchActive, setMentionSearchActive] = useState(false);
    const [mentionQuery, setMentionQuery] = useState("");
    const [mentionStartIndex, setMentionStartIndex] = useState(-1);
    const [mentionResults, setMentionResults] = useState<any[]>([]);
    const [loadingMention, setLoadingMention] = useState(false);

    // Draft states per chat room
    const [roomInputs, setRoomInputs] = useState<Record<string, string>>({});
    const [roomFiles, setRoomFiles] = useState<Record<string, File | null>>({});
    
    // Group creation/management
    const [groupName, setGroupName] = useState("");
    const [selectedFriendUids, setSelectedFriendUids] = useState<string[]>([]);
    
    // Group editing
    const [editGroupName, setEditGroupName] = useState("");
    const [editGroupFile, setEditGroupFile] = useState<File | null>(null);
    const [editGroupFilePreview, setEditGroupFilePreview] = useState<string | null>(null);
    const [updatingGroupDetails, setUpdatingGroupDetails] = useState(false);
    
    // Loading states
    const [loadingChats, setLoadingChats] = useState(false);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [loadingFriends, setLoadingFriends] = useState(false);

    const currentUser = auth.currentUser;
    const currentUserId = currentUser?.uid;

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const editFileInputRef = useRef<HTMLInputElement>(null);
    const chatInputRef = useRef<HTMLInputElement>(null);
    const cancelledHashIndex = useRef<number>(-1);

    const historyLevelRef = useRef<number>(0);
    const isClosingRef = useRef<boolean>(false);

    // Helper functions for mobile history navigation
    const pushChatState = (level: number, viewVal: any, roomIdVal: string | null = null, extra: any = {}) => {
        const isMobile = window.innerWidth < 1200;
        if (isMobile) {
            window.history.pushState({
                squadflixChat: true,
                level,
                view: viewVal,
                roomId: roomIdVal,
                ...extra
            }, '');
            historyLevelRef.current = level;
        }
    };

    const handleBackNavigation = (fallbackAction: () => void) => {
        const isMobile = window.innerWidth < 1200;
        if (isMobile && historyLevelRef.current > 0) {
            window.history.back();
        } else {
            fallbackAction();
        }
    };

    const closeChatWidget = () => {
        const isMobile = window.innerWidth < 1200;
        if (isMobile && historyLevelRef.current > 0) {
            isClosingRef.current = true;
            window.history.go(-historyLevelRef.current);
            historyLevelRef.current = 0;
        }
        setIsOpen(false);
        setView('list');
        setActiveRoomId(null);
        setPreviewAttachmentUrl(null);
    };

    const toggleChatWidget = () => {
        if (isOpen) {
            closeChatWidget();
        } else {
            pushChatState(1, 'list');
            setIsOpen(true);
            setView('list');
            setActiveRoomId(null);
        }
    };

    const openLightbox = (url: string, name: string, id: string, mime: string) => {
        setPreviewAttachmentUrl(url);
        setPreviewAttachmentName(name);
        setPreviewAttachmentId(id);
        setPreviewAttachmentMime(mime);
        pushChatState(3, view, activeRoomId, {
            lightboxActive: true,
            fileName: name,
            attachmentId: id,
            mimeType: mime,
            url
        });
    };

    // Popstate event listener for back gestures
    useEffect(() => {
        const handlePopState = (event: PopStateEvent) => {
            const state = event.state;
            const isMobile = window.innerWidth < 1200;
            if (!isMobile) return;

            if (isClosingRef.current) {
                if (!state || !state.squadflixChat) {
                    isClosingRef.current = false;
                }
                return;
            }

            if (state && state.squadflixChat) {
                historyLevelRef.current = state.level;
                setIsOpen(true);
                
                if (state.level === 1) {
                    setView('list');
                    setActiveRoomId(null);
                    setPreviewAttachmentUrl(null);
                } else if (state.level === 2) {
                    setView(state.view || 'chat');
                    setActiveRoomId(state.roomId || null);
                    setPreviewAttachmentUrl(null);
                } else if (state.level === 3) {
                    if (state.lightboxActive) {
                        setPreviewAttachmentUrl(state.url || null);
                        setPreviewAttachmentName(state.fileName || "");
                        setPreviewAttachmentId(state.attachmentId || null);
                        setPreviewAttachmentMime(state.mimeType || "");
                    } else {
                        setPreviewAttachmentUrl(null);
                    }
                    setView(state.view || 'chat');
                    setActiveRoomId(state.roomId || null);
                    if (state.userSummary) {
                        setSelectedProfileUser(state.userSummary);
                    }
                }
            } else {
                historyLevelRef.current = 0;
                setIsOpen(false);
                setView('list');
                setActiveRoomId(null);
                setPreviewAttachmentUrl(null);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    // Dynamic unread count badge shown on FAB
    const totalUnreadCount = useMemo(() => {
        return Object.values(unreadCounts).reduce((acc, count) => acc + count, 0);
    }, [unreadCounts]);

    // Hide chat widget on playback routes
    const isPlaybackRoute = /^\/squadflix\/(movies\/\d+\/play|series\/\d+\/\d+\/\d+)/.test(location.pathname) || location.pathname.includes('/stream');

    // Handle group edit file preview cleanup
    useEffect(() => {
        if (!editGroupFile) {
            setEditGroupFilePreview(null);
            return;
        }
        const url = URL.createObjectURL(editGroupFile);
        setEditGroupFilePreview(url);
        return () => URL.revokeObjectURL(url);
    }, [editGroupFile]);

    // Fetch rooms list
    const fetchChats = async (silent = false) => {
        if (!silent) setLoadingChats(true);
        try {
            const res = await SquadflixChatApi.listChats();
            if (res.success) {
                setChats(res.chats || []);
            }
        } catch (error) {
            console.error("Error fetching chats list:", error);
        } finally {
            if (!silent) setLoadingChats(false);
        }
    };

    // Fetch messages for active room
    const fetchMessages = async (roomId: string) => {
        setLoadingMessages(true);
        try {
            const res = await SquadflixChatApi.getChatMessages({ roomId, limit: 50 });
            if (res.success) {
                // The API returns newest first, so we reverse to show oldest first in chat UI
                setMessages((res.messages || []).reverse());
                // Mark as read locally
                setUnreadCounts(prev => {
                    const copy = { ...prev };
                    delete copy[roomId];
                    return copy;
                });
            }
        } catch (error) {
            console.error("Error fetching messages:", error);
            toast.error("Error al cargar los mensajes");
        } finally {
            setLoadingMessages(false);
        }
    };

    // Load list of friends for chat creation
    const fetchFriends = async () => {
        setLoadingFriends(true);
        try {
            const res = await SquadflixFriendsApi.listFriends();
            if (res.success) {
                setFriends(res.friends || []);
            }
        } catch (error) {
            console.error("Error fetching friends:", error);
        } finally {
            setLoadingFriends(false);
        }
    };

    // Fetch online friends from API as fallback
    const fetchOnlineFriends = async () => {
        try {
            const res = await SquadflixChatApi.getOnlineFriends();
            if (res.success && res.onlineFriends) {
                const uids = res.onlineFriends.map(f => f.uid);
                setOnlineFriendsUids(uids);
            }
        } catch (error) {
            console.error("Error fetching online friends:", error);
        }
    };

    // Triggered on widget open
    useEffect(() => {
        if (isOpen && currentUserId) {
            fetchChats();
            fetchOnlineFriends();
        }
    }, [isOpen, currentUserId]);

    const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
        messagesEndRef.current?.scrollIntoView({ behavior });
    };

    // Keep scrolled to bottom
    useEffect(() => {
        scrollToBottom('smooth');
    }, [messages]);

    // Manage socket events using useSocketEvents
    useSocketEvents({
        connected: !!currentUserId,
        events: {
            chat_new_message: (msg: ChatMessage) => {
                if (msg.room_id === activeRoomId) {
                    setMessages(prev => {
                        if (prev.some(m => m.id === msg.id)) return prev;
                        return [...prev, msg];
                    });
                    // Mark as read since it's open
                    setUnreadCounts(prev => {
                        const copy = { ...prev };
                        delete copy[msg.room_id];
                        return copy;
                    });
                } else {
                    // Update unread count
                    setUnreadCounts(prev => ({
                        ...prev,
                        [msg.room_id]: (prev[msg.room_id] || 0) + 1
                    }));

                    // Show Toast if sender is not current user
                    if (msg.sender_uid !== currentUserId) {
                        const roomName = getRoomDisplayName(chats.find(c => c.id === msg.room_id), msg);
                        toast.info(
                            <div>
                                <strong>{msg.sender.displayName} ({roomName}):</strong>
                                <p style={{ margin: 0, fontSize: '0.85rem' }}>
                                    {msg.has_attachment ? "📁 Archivo adjunto" : msg.content}
                                </p>
                            </div>
                        );
                    }
                }
                // Silently refresh chats to show new snippet
                fetchChats(true);
            },
            online_friends: (uids: string[]) => {
                setOnlineFriendsUids(uids);
            },
            friend_status_change: (data: { uid: string; online: boolean }) => {
                setOnlineFriendsUids(prev => {
                    if (data.online) {
                        return prev.includes(data.uid) ? prev : [...prev, data.uid];
                    } else {
                        return prev.filter(uid => uid !== data.uid);
                    }
                });
            }
        }
    });

    // Helper: determine room title
    const getRoomDisplayName = (room?: ChatRoomDetails, fallbackMsg?: ChatMessage) => {
        if (!room) return fallbackMsg?.sender.displayName || "Chat";
        if (room.is_group) return room.name || "Grupo";
        const otherMember = room.members.find(m => m.uid !== currentUserId);
        return otherMember?.displayName || "Usuario de SquadDisk";
    };

    // Group photo loading helper
    const loadGroupPhotoUrl = async (roomId: string) => {
        if (groupPhotoUrls[roomId]) return;
        
        setGroupPhotoUrls(prev => ({ ...prev, [roomId]: 'loading' }));
        
        try {
            const res = await SquadflixChatApi.getGroupPhoto({ roomId });
            if (!res.ok) {
                throw new Error(`Failed to load group photo with status: ${res.status}`);
            }
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            setGroupPhotoUrls(prev => ({ ...prev, [roomId]: url }));
        } catch (error) {
            console.error("Error loading group photo:", error);
            setGroupPhotoUrls(prev => ({ ...prev, [roomId]: 'failed' }));
        }
    };

    // Helper: determine room photo URL
    const getRoomPhotoURL = (room?: ChatRoomDetails) => {
        if (!room) return null;
        if (room.is_group) {
            if (room.photoUrl) {
                const loadedUrl = groupPhotoUrls[room.id];
                if (!loadedUrl) {
                    loadGroupPhotoUrl(room.id);
                }
                if (loadedUrl && loadedUrl !== 'loading' && loadedUrl !== 'failed') {
                    return loadedUrl;
                }
            }
            return null;
        }
        const otherMember = room.members.find(m => m.uid !== currentUserId);
        return otherMember?.photoURL ? getCachedImageUrl(otherMember.photoURL) : null;
    };

    // Helper: check online status of 1-to-1 chat friend
    const isRoomOnline = (room?: ChatRoomDetails) => {
        if (!room || room.is_group) return false;
        const otherMember = room.members.find(m => m.uid !== currentUserId);
        return otherMember ? onlineFriendsUids.includes(otherMember.uid) : false;
    };

    // Attachment blob loading helper
    const loadAttachmentUrl = async (attachmentId: string) => {
        if (attachmentUrls[attachmentId]) return;
        
        // Mark as loading immediately to prevent duplicate fetches on quick re-renders
        setAttachmentUrls(prev => ({ ...prev, [attachmentId]: 'loading' }));
        
        try {
            const res = await SquadflixChatApi.getChatAttachment({ attachmentId });
            if (!res.ok) {
                throw new Error(`Failed to load attachment with status: ${res.status}`);
            }
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            setAttachmentUrls(prev => ({ ...prev, [attachmentId]: url }));
        } catch (error) {
            console.error("Error loading image attachment:", error);
            setAttachmentUrls(prev => ({ ...prev, [attachmentId]: 'failed' }));
        }
    };

    const handleDownloadAttachment = async (attachmentId: string, fileName: string) => {
        try {
            toast.info(`Descargando ${fileName}...`);
            const res = await SquadflixChatApi.getChatAttachment({ attachmentId });
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading attachment:", error);
            toast.error("No se pudo descargar el archivo");
        }
    };

    const handlePreviewAttachment = async (attachmentId: string, fileName: string, mimeType: string) => {
        // If we already have the blob URL cached, use it
        if (attachmentUrls[attachmentId] && attachmentUrls[attachmentId] !== 'loading' && attachmentUrls[attachmentId] !== 'failed') {
            openLightbox(attachmentUrls[attachmentId], fileName, attachmentId, mimeType);
            return;
        }

        // Otherwise load it on demand
        toast.info("Cargando vista previa...");
        try {
            const res = await SquadflixChatApi.getChatAttachment({ attachmentId });
            if (!res.ok) throw new Error("Status: " + res.status);
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            
            // Cache it so we don't load again
            setAttachmentUrls(prev => ({ ...prev, [attachmentId]: url }));
            
            openLightbox(url, fileName, attachmentId, mimeType);
        } catch (error) {
            console.error("Error loading preview:", error);
            toast.error("No se pudo previsualizar el archivo");
        }
    };

    const handleAttachmentClick = (attachmentId: string, fileName: string, mimeType: string) => {
        const isPreviewable = mimeType.startsWith("image/") || 
                              mimeType.startsWith("video/") || 
                              mimeType.startsWith("audio/") || 
                              mimeType === "application/pdf";
                               
        if (isPreviewable) {
            handlePreviewAttachment(attachmentId, fileName, mimeType);
        } else {
            handleDownloadAttachment(attachmentId, fileName);
        }
    };

    // Debounced search for movies and series when typing '#'
    useEffect(() => {
        if (!mentionSearchActive || !mentionQuery.trim()) {
            setMentionResults([]);
            return;
        }

        setLoadingMention(true);
        const delayDebounce = setTimeout(async () => {
            try {
                const moviesData = await searchPage(mentionQuery, 1, 'movies');
                const seriesData = await searchPage(mentionQuery, 1, 'series');
                
                const movies = (moviesData?.results || []).map((m: any) => ({ ...m, isMovie: true }));
                const series = (seriesData?.results || []).map((s: any) => ({ ...s, isMovie: false }));
                
                const combined = [...movies.slice(0, 5), ...series.slice(0, 5)];
                setMentionResults(combined);
            } catch (error) {
                console.error("Error searching mentions:", error);
            } finally {
                setLoadingMention(false);
            }
        }, 300);

        return () => clearTimeout(delayDebounce);
    }, [mentionQuery, mentionSearchActive]);

    const handleSelectMention = (item: any) => {
        if (!chatInputRef.current) return;
        
        const title = item.title || item.name || "";
        const type = item.isMovie ? 'movie' : 'tv';
        const mentionToken = `{{${type}-${item.id}:${title}}}`;
        const beforeMention = inputText.substring(0, mentionStartIndex);
        const afterMention = inputText.substring(chatInputRef.current.selectionStart || 0);
        
        const newText = beforeMention + mentionToken + " " + afterMention;
        setInputText(newText);
        if (activeRoomId) {
            setRoomInputs(prev => ({ ...prev, [activeRoomId]: newText }));
        }
        
        setMentionSearchActive(false);
        setMentionResults([]);
        
        const cursorPosition = beforeMention.length + mentionToken.length + 1;
        setTimeout(() => {
            if (chatInputRef.current) {
                chatInputRef.current.focus();
                chatInputRef.current.setSelectionRange(cursorPosition, cursorPosition);
            }
        }, 10);
    };

    // Render message text with inline mention chips
    const renderMessageContent = (content: string, clickable = true) => {
        const mentionRegex = /\{\{(movie|tv)-(\d+):(.+?)\}\}/g;
        const parts: React.ReactNode[] = [];
        let lastIndex = 0;
        let match;

        while ((match = mentionRegex.exec(content)) !== null) {
            // Push plain text before the match
            if (match.index > lastIndex) {
                parts.push(content.substring(lastIndex, match.index));
            }
            const [, type, id, title] = match;
            const route = type === 'movie' ? `/squadflix/movies/${id}` : `/squadflix/series/${id}`;
            parts.push(
                <span
                    key={`mention-${match.index}`}
                    className={`squadflix-chat-widget__mention-chip squadflix-chat-widget__mention-chip--${type}${!clickable ? ' squadflix-chat-widget__mention-chip--static' : ''}`}
                    onClick={clickable ? (e) => {
                        e.stopPropagation();
                        closeChatWidget();
                        setTimeout(() => {
                            navigate(route);
                        }, 50);
                    } : undefined}
                    title={`${type === 'movie' ? 'Película' : 'Serie'}: ${title}`}
                >
                    <i className={type === 'movie' ? 'fa-solid fa-film' : 'fa-solid fa-tv'} />
                    {title}
                </span>
            );
            lastIndex = match.index + match[0].length;
        }

        // Push remaining plain text
        if (lastIndex < content.length) {
            parts.push(content.substring(lastIndex));
        }

        return parts.length > 0 ? parts : content;
    };

    const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputText(value);
        if (activeRoomId) {
            setRoomInputs(prev => ({ ...prev, [activeRoomId]: value }));
        }

        const selectionStart = e.target.selectionStart || 0;
        const lastHashIndex = value.lastIndexOf('#', selectionStart - 1);

        if (lastHashIndex !== -1) {
            const charBeforeHash = lastHashIndex > 0 ? value[lastHashIndex - 1] : '';
            const isValidStart = lastHashIndex === 0 || charBeforeHash === ' ' || charBeforeHash === '\n';
            
            if (isValidStart) {
                if (cancelledHashIndex.current === lastHashIndex) {
                    setMentionSearchActive(false);
                    return;
                }

                const query = value.substring(lastHashIndex + 1, selectionStart);
                setMentionSearchActive(true);
                setMentionQuery(query);
                setMentionStartIndex(lastHashIndex);
                return;
            }
        }
        
        setMentionSearchActive(false);
        setMentionQuery("");
        setMentionStartIndex(-1);
    };

    const handleKeyDownInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Reset cancellation when user types a new #
        if (e.key === '#') {
            cancelledHashIndex.current = -1;
        }
        if (e.key === 'Escape' && mentionSearchActive) {
            e.preventDefault();
            e.stopPropagation();
            setMentionSearchActive(false);
            cancelledHashIndex.current = mentionStartIndex;
        }
    };

    // Send text / file message
    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!activeRoomId) return;

        const text = inputText.trim();
        if (!text && !selectedFile) return;

        setSendingMessage(true);
        try {
            const res = await SquadflixChatApi.sendMessage({
                roomId: activeRoomId,
                messageText: text || undefined,
                file: selectedFile || undefined
            });

            if (res.success) {
                setInputText("");
                setSelectedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
                // Clear room drafts after successful send
                setRoomInputs(prev => { const next = { ...prev }; delete next[activeRoomId]; return next; });
                setRoomFiles(prev => { const next = { ...prev }; delete next[activeRoomId]; return next; });
                // No need to fetch chats or messages manually here;
                // the chat_new_message socket event will deliver the new message
                // and trigger a list update automatically.
            }
        } catch (error) {
            console.error("Error sending message:", error);
            toast.error("No se pudo enviar el mensaje");
        } finally {
            setSendingMessage(false);
        }
    };

    // Open chat room
    const handleSelectRoom = (roomId: string) => {
        setInputText(roomInputs[roomId] || "");
        setSelectedFile(roomFiles[roomId] || null);
        setActiveRoomId(roomId);
        setView('chat');
        fetchMessages(roomId);
        pushChatState(2, 'chat', roomId);
    };

    // Start private chat with friend
    const handleStartPrivateChat = async (targetUid: string) => {
        try {
            const res = await SquadflixChatApi.createPrivateChat({ targetUid });
            if (res.success) {
                fetchChats(true);
                handleSelectRoom(res.roomId);
            }
        } catch (error) {
            console.error("Error starting private chat:", error);
            toast.error("No se pudo iniciar el chat privado");
        }
    };

    // Create group chat
    const handleCreateGroupChat = async (e: React.FormEvent) => {
        e.preventDefault();
        const name = groupName.trim();
        if (!name) {
            toast.warning("Ingresa un nombre para el grupo");
            return;
        }

        try {
            const res = await SquadflixChatApi.createChatGroup({
                name,
                invitedUids: selectedFriendUids
            });

            if (res.success) {
                toast.success("¡Grupo de chat creado!");
                setGroupName("");
                setSelectedFriendUids([]);
                fetchChats(true);
                handleSelectRoom(res.roomId);
            }
        } catch (error) {
            console.error("Error creating group:", error);
            toast.error("No se pudo crear el grupo de chat");
        }
    };

    // Add member to group
    const handleAddMemberToGroup = async (targetUid: string) => {
        if (!activeRoomId) return;
        try {
            const res = await SquadflixChatApi.addMemberToGroup({
                roomId: activeRoomId,
                targetUid
            });

            if (res.success) {
                toast.success("Miembro agregado correctamente");
                fetchChats(true);
                handleBackNavigation(() => setView('chat'));
            }
        } catch (error) {
            console.error("Error adding member:", error);
            toast.error("No se pudo agregar al miembro");
        }
    };

    // Leave group chat
    const handleLeaveGroup = async () => {
        if (!activeRoomId) return;
        if (!window.confirm("¿Seguro que deseas salir de este grupo de chat?")) return;

        try {
            await SquadflixChatApi.leaveChatGroup({ roomId: activeRoomId });
            toast.info("Has abandonado el grupo");
            setActiveRoomId(null);
            setView('list');
            fetchChats();
        } catch (error) {
            console.error("Error leaving group:", error);
            toast.error("No se pudo salir del grupo");
        }
    };

    // Update group name and/or profile picture
    const handleUpdateGroupDetails = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!activeRoomId) return;

        const name = editGroupName.trim();
        if (!name && !editGroupFile) {
            toast.warning("Modifica al menos el nombre o selecciona una foto de perfil");
            return;
        }

        setUpdatingGroupDetails(true);
        try {
            const res = await SquadflixChatApi.updateGroupDetails({
                roomId: activeRoomId,
                name: name || undefined,
                file: editGroupFile || undefined
            });

            if (res.success) {
                toast.success("Detalles del grupo actualizados correctamente");
                setEditGroupFile(null);
                if (editFileInputRef.current) editFileInputRef.current.value = "";
                
                // Clear the cached group photo for this room so it reloads the new one
                if (res.photoUrl) {
                    setGroupPhotoUrls(prev => {
                        const copy = { ...prev };
                        delete copy[activeRoomId];
                        return copy;
                    });
                }
                
                // Refresh chats list to get the updated name/photoUrl
                await fetchChats(true);
                
                // Go back to chat view
                handleBackNavigation(() => setView('chat'));
            }
        } catch (error) {
            console.error("Error updating group details:", error);
            toast.error("No se pudieron actualizar los detalles del grupo");
        } finally {
            setUpdatingGroupDetails(false);
        }
    };

    const handleNavigateToProfile = (user: UserSummary) => {
        closeChatWidget();
        const isAuthedUser = currentUserId === user.uid;
        setTimeout(() => {
            if (isAuthedUser) {
                navigate(`/my-profile/posts`);
            } else {
                navigate(`/user/${user.displayName}/${user.userCode}/posts`);
            }
        }, 50);
    };

    const handleShowUserProfile = async (user: UserSummary) => {
        setSelectedProfileUser(user);
        setDetailedUserProfile(null);
        setView('user-profile');
        pushChatState(3, 'user-profile', activeRoomId, { userSummary: user });
        try {
            const res = await SquadDiskUsersApi.findUserDetails({
                userName: user.displayName,
                code: user.userCode
            });
            if (res && res.user) {
                setDetailedUserProfile(res.user);
            }
        } catch (error) {
            console.error("Error loading detailed user profile:", error);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setSelectedFile(file);
            if (activeRoomId) {
                setRoomFiles(prev => ({ ...prev, [activeRoomId]: file }));
            }
        }
    };

    const formatBytes = (bytes: number, decimals = 2) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    };

    const formatTime = (isoString?: string) => {
        if (!isoString) return "";
        const date = new Date(isoString);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    // Filter rooms by Tab and Search Query
    const filteredChats = useMemo(() => {
        return chats.filter(room => {
            if (currentTab === 'private' && room.is_group) return false;
            if (currentTab === 'group' && !room.is_group) return false;
            
            const name = getRoomDisplayName(room).toLowerCase();
            return name.includes(searchQuery.toLowerCase());
        });
    }, [chats, currentTab, searchQuery, onlineFriendsUids]);

    const activeRoom = chats.find(c => c.id === activeRoomId);

    // List of friends that can be invited to add member (filtering current members)
    const friendsToInvite = useMemo(() => {
        if (!activeRoom) return [];
        return friends.filter(f => !activeRoom.members.some(m => m.uid === f.uid));
    }, [friends, activeRoom]);

    if (!currentUserId || isPlaybackRoute) return null;

    return (
        <div className={`squadflix-chat-widget ${!darkMode ? 'squadflix-chat-widget--light' : ''}`}>
            {/* FAB Button */}
            <button 
                className="squadflix-chat-widget__fab" 
                onClick={toggleChatWidget}
                title="Abrir Chats"
            >
                {isOpen ? <i className="fa-solid fa-xmark"></i> : <i className="fa-solid fa-comments"></i>}
                {!isOpen && totalUnreadCount > 0 && (
                    <span className="squadflix-chat-widget__fab-badge">{totalUnreadCount}</span>
                )}
            </button>

            {/* Chat Modal Panel */}
            {isOpen && (
                <div className="squadflix-chat-widget__panel">
                    
                    {/* View: Directory Chats list */}
                    {view === 'list' && (
                        <>
                            <div className="squadflix-chat-widget__header">
                                <div className="d-flex align-items-center gap-2">
                                    <button 
                                        className="squadflix-chat-widget__header-btn squadflix-chat-widget__close-x"
                                        onClick={closeChatWidget}
                                        title="Cerrar chat"
                                    >
                                        <i className="fa-solid fa-xmark"></i>
                                    </button>
                                    <h3 className="squadflix-chat-widget__header-title">
                                        <i className="fa-solid fa-comments text-primary"></i> Chats
                                    </h3>
                                </div>
                                <div className="squadflix-chat-widget__header-actions">
                                    <button 
                                        className="squadflix-chat-widget__header-btn"
                                        onClick={() => {
                                            fetchFriends();
                                            setView('create-private');
                                            pushChatState(2, 'create-private');
                                        }}
                                        title="Nuevo Chat Privado"
                                    >
                                        <i className="fa-solid fa-user-plus"></i>
                                    </button>
                                    <button 
                                        className="squadflix-chat-widget__header-btn"
                                        onClick={() => {
                                            fetchFriends();
                                            setView('create-group');
                                            pushChatState(2, 'create-group');
                                        }}
                                        title="Nuevo Grupo de Chat"
                                    >
                                        <i className="fa-solid fa-users-gear"></i>
                                    </button>
                                </div>
                            </div>

                            <div className="squadflix-chat-widget__body">
                                {/* Search input */}
                                <input 
                                    type="text" 
                                    className="form-control squadflix-chat-widget__directory-search mb-2"
                                    placeholder="Buscar chat..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    style={{
                                        backgroundColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
                                        border: darkMode ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.12)",
                                        color: darkMode ? "white" : "black"
                                    }}
                                />

                                {/* Tabs filter */}
                                <div className="squadflix-chat-widget__directory-tabs">
                                    <button 
                                        className={`squadflix-chat-widget__directory-tab ${currentTab === 'all' ? 'active' : ''}`}
                                        onClick={() => setCurrentTab('all')}
                                    >
                                        Todos
                                    </button>
                                    <button 
                                        className={`squadflix-chat-widget__directory-tab ${currentTab === 'private' ? 'active' : ''}`}
                                        onClick={() => setCurrentTab('private')}
                                    >
                                        Privados
                                    </button>
                                    <button 
                                        className={`squadflix-chat-widget__directory-tab ${currentTab === 'group' ? 'active' : ''}`}
                                        onClick={() => setCurrentTab('group')}
                                    >
                                        Grupos
                                    </button>
                                </div>

                                {loadingChats ? (
                                    <div className="d-flex justify-content-center py-5">
                                        <Spinner />
                                    </div>
                                ) : filteredChats.length === 0 ? (
                                    <div className="squadflix-chat-widget__empty text-center py-5">
                                        <i className="fa-solid fa-comment-slash"></i>
                                        <div className="squadflix-chat-widget__empty-title">No hay chats</div>
                                        <p className="text-muted small">Crea un chat con un amigo para comenzar.</p>
                                    </div>
                                ) : (
                                    <div className="d-flex flex-column gap-1">
                                        {filteredChats.map(room => {
                                            const isOnline = isRoomOnline(room);
                                            const roomPhoto = getRoomPhotoURL(room);
                                            const roomTitle = getRoomDisplayName(room);
                                            const unreadCount = unreadCounts[room.id] || 0;
                                            
                                            return (
                                                <div 
                                                    key={room.id} 
                                                    className="squadflix-chat-widget__directory-item"
                                                    onClick={() => handleSelectRoom(room.id)}
                                                >
                                                    <div className="squadflix-chat-widget__directory-avatar-container">
                                                        <img 
                                                            src={roomPhoto || avatarDefault} 
                                                            alt={roomTitle} 
                                                            className="squadflix-chat-widget__directory-avatar"
                                                        />
                                                        {!room.is_group && (
                                                            <span className={`squadflix-chat-widget__directory-dot ${isOnline ? 'squadflix-chat-widget__directory-dot--online' : 'squadflix-chat-widget__directory-dot--offline'}`} />
                                                        )}
                                                    </div>
                                                    <div className="squadflix-chat-widget__directory-info">
                                                        <div className="squadflix-chat-widget__directory-name-row">
                                                            <h4 className="squadflix-chat-widget__directory-name">
                                                                {room.is_group && (
                                                                    <span className="badge bg-primary rounded-circle d-inline-flex align-items-center justify-content-center" style={{ fontSize: '0.6rem', width: 16, height: 16, padding: 0, marginRight: 6, verticalAlign: 'middle' }}>
                                                                        <i className="fa-solid fa-users"></i>
                                                                    </span>
                                                                )}
                                                                <span style={{ verticalAlign: 'middle' }}>{roomTitle}</span>
                                                            </h4>
                                                            {room.lastMessage && (
                                                                <span className="squadflix-chat-widget__directory-time">
                                                                    {formatTime(room.lastMessage.created_at)}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="squadflix-chat-widget__directory-snippet-row">
                                                            <p className="squadflix-chat-widget__directory-snippet">
                                                                {room.lastMessage ? (
                                                                    <>{room.lastMessage.sender.uid === currentUserId ? 'Tú: ' : ''}{renderMessageContent(room.lastMessage.content || '📁 Archivo', false)}</>
                                                                ) : (
                                                                    <span className="text-muted italic">No hay mensajes aún</span>
                                                                )}
                                                            </p>
                                                            {unreadCount > 0 && (
                                                                <span className="badge bg-danger rounded-pill px-2 py-1" style={{ fontSize: '0.7rem' }}>
                                                                    {unreadCount}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            <button 
                                className="squadflix-chat-widget__close-fab"
                                onClick={closeChatWidget}
                                title="Cerrar chat"
                            >
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </>
                    )}

                    {/* View: Chat Room Panel */}
                    {view === 'chat' && activeRoomId && (
                        <>
                            <div className="squadflix-chat-widget__header">
                                <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                                    <button 
                                        className="squadflix-chat-widget__header-btn"
                                        onClick={() => handleBackNavigation(() => {
                                            setView('list');
                                            setActiveRoomId(null);
                                            fetchChats(true);
                                        })}
                                    >
                                        <i className="fa-solid fa-arrow-left"></i>
                                    </button>
                                    <div 
                                        className="d-flex align-items-center gap-2 squadflix-chat-widget__header-clickable"
                                        style={{ minWidth: 0, cursor: 'pointer' }}
                                        onClick={() => {
                                            if (activeRoom) {
                                                if (activeRoom.is_group) {
                                                    setEditGroupName(activeRoom.name || "");
                                                    setEditGroupFile(null);
                                                    setView('edit-group');
                                                    pushChatState(3, 'edit-group', activeRoomId);
                                                } else {
                                                    const otherMember = activeRoom.members.find(m => m.uid !== currentUserId);
                                                    if (otherMember) {
                                                        handleShowUserProfile(otherMember);
                                                    }
                                                }
                                            }
                                        }}
                                        title={activeRoom?.is_group ? "Editar detalles del grupo" : "Ver perfil"}
                                    >
                                        <div className="squadflix-chat-widget__directory-avatar-container" style={{ width: 36, height: 36 }}>
                                            <img 
                                                src={getRoomPhotoURL(activeRoom) || avatarDefault} 
                                                alt={getRoomDisplayName(activeRoom)} 
                                                className="squadflix-chat-widget__directory-avatar"
                                            />
                                            {activeRoom && !activeRoom.is_group && (
                                                <span className={`squadflix-chat-widget__directory-dot ${isRoomOnline(activeRoom) ? 'squadflix-chat-widget__directory-dot--online' : 'squadflix-chat-widget__directory-dot--offline'}`} style={{ width: 10, height: 10 }} />
                                            )}
                                        </div>
                                        <div style={{ minWidth: 0 }}>
                                            <h4 className="squadflix-chat-widget__directory-name" style={{ fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <span>{getRoomDisplayName(activeRoom)}</span>
                                                {activeRoom?.is_group && <i className="fa-solid fa-pen squadflix-chat-widget__edit-indicator" style={{ fontSize: '0.75rem', opacity: 0.5 }}></i>}
                                            </h4>
                                            <span className="squadflix-chat-widget__header-subtitle">
                                                {activeRoom?.is_group ? `${activeRoom.members.length} miembros` : isRoomOnline(activeRoom) ? 'En línea' : 'Desconectado'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="squadflix-chat-widget__header-actions">
                                    {activeRoom?.is_group && (
                                        <>
                                            <button 
                                                className="squadflix-chat-widget__header-btn"
                                                onClick={() => {
                                                    fetchFriends();
                                                    setView('add-member');
                                                    pushChatState(3, 'add-member', activeRoomId);
                                                }}
                                                title="Añadir Miembro"
                                            >
                                                <i className="fa-solid fa-user-plus"></i>
                                            </button>
                                            <button 
                                                className="squadflix-chat-widget__header-btn text-danger"
                                                onClick={handleLeaveGroup}
                                                title="Salir del Grupo"
                                            >
                                                <i className="fa-solid fa-door-open"></i>
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Messages area */}
                            <div className="squadflix-chat-widget__messages-list">
                                {loadingMessages ? (
                                    <div className="d-flex justify-content-center align-items-center h-100">
                                        <Spinner />
                                    </div>
                                ) : messages.length === 0 ? (
                                    <div className="squadflix-chat-widget__empty text-center py-5">
                                        <i className="fa-solid fa-message opacity-50"></i>
                                        <div className="squadflix-chat-widget__empty-title">Sin mensajes</div>
                                        <p className="text-muted small">Envía un mensaje para comenzar la conversación.</p>
                                    </div>
                                ) : (
                                    messages.map(msg => {
                                        const isSentByMe = msg.sender_uid === currentUserId;
                                        
                                        // Inline media renderer loader
                                        let mediaElement = null;
                                        if (msg.has_attachment && msg.attachment) {
                                            const isImage = msg.attachment.mime_type.startsWith("image/");
                                            const isVideo = msg.attachment.mime_type.startsWith("video/");
                                            if (isImage || isVideo) {
                                                const attachmentId = msg.attachment.id;
                                                if (!attachmentUrls[attachmentId]) {
                                                    loadAttachmentUrl(attachmentId);
                                                }
                                                const previewUrl = attachmentUrls[attachmentId];
                                                if (previewUrl === 'loading') {
                                                    mediaElement = (
                                                        <div className="p-2 text-center text-muted small">
                                                            <Spinner className="spinner-sm" /> Cargando {isImage ? 'imagen' : 'video'}...
                                                        </div>
                                                    );
                                                } else if (previewUrl === 'failed') {
                                                    mediaElement = (
                                                        <div className="p-2 text-center text-danger small" style={{ opacity: 0.8 }}>
                                                            <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: '6px' }}></i> No se pudo cargar la {isImage ? 'imagen' : 'video'}
                                                        </div>
                                                    );
                                                } else if (previewUrl) {
                                                    if (isImage) {
                                                        mediaElement = (
                                                            <div className="squadflix-chat-widget__attachment">
                                                                <img 
                                                                    src={previewUrl} 
                                                                    alt={msg.attachment.file_name} 
                                                                    className="squadflix-chat-widget__attachment-img"
                                                                    onClick={() => {
                                                                        openLightbox(previewUrl, msg.attachment!.file_name, msg.attachment!.id, msg.attachment!.mime_type);
                                                                    }}
                                                                    onLoad={() => scrollToBottom('smooth')}
                                                                />
                                                            </div>
                                                        );
                                                    } else {
                                                        mediaElement = (
                                                            <div className="squadflix-chat-widget__attachment squadflix-chat-widget__attachment--video">
                                                                <video 
                                                                    src={previewUrl} 
                                                                    className="squadflix-chat-widget__attachment-video"
                                                                    preload="metadata"
                                                                    muted
                                                                    playsInline
                                                                    onClick={() => {
                                                                        openLightbox(previewUrl, msg.attachment!.file_name, msg.attachment!.id, msg.attachment!.mime_type);
                                                                    }}
                                                                    onLoadedData={() => scrollToBottom('smooth')}
                                                                />
                                                                <div 
                                                                    className="squadflix-chat-widget__attachment-video-overlay"
                                                                    onClick={() => {
                                                                        openLightbox(previewUrl, msg.attachment!.file_name, msg.attachment!.id, msg.attachment!.mime_type);
                                                                    }}
                                                                >
                                                                    <i className="fa-solid fa-play"></i>
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                } else {
                                                    mediaElement = (
                                                        <div className="p-2 text-center text-muted small">
                                                            <Spinner className="spinner-sm" /> Cargando {isImage ? 'imagen' : 'video'}...
                                                        </div>
                                                    );
                                                }
                                            }
                                        }

                                        return (
                                            <div 
                                                key={msg.id} 
                                                className={`squadflix-chat-widget__message-row ${isSentByMe ? 'squadflix-chat-widget__message-row--sent' : 'squadflix-chat-widget__message-row--received'}`}
                                            >
                                                {!isSentByMe && (
                                                    <img 
                                                        src={msg.sender.photoURL ? getCachedImageUrl(msg.sender.photoURL) : avatarDefault} 
                                                        alt={msg.sender.displayName} 
                                                        className="squadflix-chat-widget__message-avatar"
                                                        onClick={() => handleShowUserProfile(msg.sender)}
                                                        style={{ cursor: 'pointer' }}
                                                        title={`Ver perfil de ${msg.sender.displayName}`}
                                                    />
                                                )}
                                                <div className="squadflix-chat-widget__message-bubble">
                                                    {/* File display */}
                                                    {mediaElement}
                                                    {msg.has_attachment && msg.attachment && !msg.attachment.mime_type.startsWith("image/") && !msg.attachment.mime_type.startsWith("video/") && (
                                                        <div className="squadflix-chat-widget__attachment">
                                                            <div 
                                                                className="squadflix-chat-widget__attachment-file"
                                                                onClick={() => handleAttachmentClick(msg.attachment!.id, msg.attachment!.file_name, msg.attachment!.mime_type)}
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                <i className="fa-solid fa-file-arrow-down"></i>
                                                                <div className="squadflix-chat-widget__attachment-file-info">
                                                                    <p className="squadflix-chat-widget__attachment-file-name">{msg.attachment.file_name}</p>
                                                                    <p className="squadflix-chat-widget__attachment-file-size">{formatBytes(msg.attachment.file_size)}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {msg.content && <div className="squadflix-chat-widget__message-text">{renderMessageContent(msg.content)}</div>}
                                                    
                                                    <div className="squadflix-chat-widget__message-meta">
                                                        {!isSentByMe && (
                                                            <span 
                                                                className="squadflix-chat-widget__message-sender"
                                                                onClick={() => handleShowUserProfile(msg.sender)}
                                                                style={{ cursor: 'pointer' }}
                                                                title={`Ver perfil de ${msg.sender.displayName}`}
                                                            >
                                                                {msg.sender.displayName}
                                                            </span>
                                                        )}
                                                        <span>{formatTime(msg.created_at)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Chat input composer */}
                            <div className="squadflix-chat-widget__footer" style={{ position: 'relative' }}>
                                {mentionSearchActive && (
                                    <div className="squadflix-chat-widget__mention-dropdown">
                                        {loadingMention ? (
                                            <div className="p-3 text-center text-muted small">
                                                <Spinner className="spinner-sm" /> Buscando película o serie...
                                            </div>
                                        ) : mentionResults.length === 0 ? (
                                            <div className="p-3 text-center text-muted small">
                                                Escribe después del # para buscar películas o series...
                                            </div>
                                        ) : (
                                            <div className="squadflix-chat-widget__mention-list">
                                                {mentionResults.map((item) => (
                                                    <div 
                                                        key={item.id} 
                                                        className="squadflix-chat-widget__mention-item"
                                                        onClick={() => handleSelectMention(item)}
                                                    >
                                                        <div className="squadflix-chat-widget__mention-poster-wrap">
                                                            {item.poster_path ? (
                                                                <img 
                                                                    src={getTmdbImage(item.poster_path, "w92")} 
                                                                    alt={item.title || item.name} 
                                                                    className="squadflix-chat-widget__mention-poster"
                                                                />
                                                            ) : (
                                                                <div className="squadflix-chat-widget__mention-poster-placeholder">
                                                                    <i className={item.isMovie ? "fa-solid fa-film" : "fa-solid fa-tv"}></i>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="squadflix-chat-widget__mention-info">
                                                            <p className="squadflix-chat-widget__mention-title">{item.title || item.name}</p>
                                                            <p className="squadflix-chat-widget__mention-type">
                                                                {item.isMovie ? (
                                                                    <span className="badge bg-primary-subtle text-primary border border-primary-subtle rounded-pill px-2 py-0" style={{ fontSize: '0.65rem' }}>
                                                                        <i className="fa-solid fa-film" style={{ marginRight: '4px' }}></i> Película
                                                                    </span>
                                                                ) : (
                                                                    <span className="badge bg-success-subtle text-success border border-success-subtle rounded-pill px-2 py-0" style={{ fontSize: '0.65rem' }}>
                                                                        <i className="fa-solid fa-tv" style={{ marginRight: '4px' }}></i> Serie
                                                                    </span>
                                                                )}
                                                                {(item.release_date || item.first_air_date) && (
                                                                    <span className="text-muted ms-2 small">
                                                                        {new Date(item.release_date || item.first_air_date).getFullYear()}
                                                                    </span>
                                                                )}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {selectedFile && (
                                    <div className="squadflix-chat-widget__upload-preview">
                                        <i className="fa-solid fa-paperclip squadflix-chat-widget__upload-preview-icon"></i>
                                        <span className="squadflix-chat-widget__upload-preview-name">{selectedFile.name} ({formatBytes(selectedFile.size)})</span>
                                        <button 
                                            type="button" 
                                            className="squadflix-chat-widget__upload-preview-remove" 
                                            onClick={() => {
                                                setSelectedFile(null);
                                                if (fileInputRef.current) fileInputRef.current.value = "";
                                                if (activeRoomId) {
                                                    setRoomFiles(prev => { const next = { ...prev }; delete next[activeRoomId]; return next; });
                                                }
                                            }}
                                        >
                                            <i className="fa-solid fa-trash"></i>
                                        </button>
                                    </div>
                                )}

                                <form onSubmit={handleSendMessage} className="squadflix-chat-widget__input-form">
                                    <input 
                                        type="file" 
                                        style={{ display: 'none' }} 
                                        ref={fileInputRef} 
                                        onChange={handleFileChange}
                                    />
                                    <button 
                                        type="button" 
                                        className="squadflix-chat-widget__input-btn"
                                        onClick={() => fileInputRef.current?.click()}
                                        title="Adjuntar archivo"
                                        disabled={sendingMessage}
                                    >
                                        <i className="fa-solid fa-paperclip"></i>
                                    </button>
                                    <div className="squadflix-chat-widget__input-wrap">
                                        <input 
                                            className="squadflix-chat-widget__input"
                                            placeholder={sendingMessage ? "Subiendo archivo..." : "Escribe un mensaje..."}
                                            value={inputText}
                                            onChange={handleChangeInput}
                                            onKeyDown={handleKeyDownInput}
                                            ref={chatInputRef}
                                            disabled={sendingMessage}
                                        />
                                    </div>
                                    <button 
                                        type="submit" 
                                        className="squadflix-chat-widget__send-btn"
                                        disabled={(!inputText.trim() && !selectedFile) || sendingMessage}
                                    >
                                        {sendingMessage ? (
                                            <Spinner className="spinner-sm" />
                                        ) : (
                                            <i className="fa-solid fa-paper-plane"></i>
                                        )}
                                    </button>
                                </form>
                            </div>
                        </>
                    )}

                    {/* View: Create Private Chat */}
                    {view === 'create-private' && (
                        <div className="squadflix-chat-widget__overlay-modal">
                            <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                                <h4 className="m-0" style={{ fontSize: '1rem', fontWeight: 700 }}>
                                    <i className="fa-solid fa-user-plus text-primary mr-1"></i> Iniciar Chat Privado
                                </h4>
                                <button className="btn-close btn-close-white" onClick={() => handleBackNavigation(() => setView('list'))}></button>
                            </div>
                            
                            {loadingFriends ? (
                                <div className="d-flex justify-content-center py-5">
                                    <Spinner />
                                </div>
                            ) : friends.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <p>Aún no tienes amigos en SquadDisk.</p>
                                    <p className="small">Agrega amigos en tu perfil para chatear.</p>
                                </div>
                            ) : (
                                <div className="squadflix-chat-widget__create-friends-list">
                                    {friends.map(friend => (
                                        <div 
                                            key={friend.uid} 
                                            className="squadflix-chat-widget__create-friend-row"
                                            onClick={() => handleStartPrivateChat(friend.uid)}
                                        >
                                            <img 
                                                src={friend.photoURL ? getCachedImageUrl(friend.photoURL) : avatarDefault} 
                                                alt={friend.displayName} 
                                                className="squadflix-chat-widget__message-avatar"
                                            />
                                            <div>
                                                <p className="m-0 font-weight-bold" style={{ fontSize: '0.9rem' }}>{friend.displayName}</p>
                                                <p className="m-0 text-muted small">#{friend.userCode}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* View: Create Group Chat */}
                    {view === 'create-group' && (
                        <div className="squadflix-chat-widget__overlay-modal">
                            <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                                <h4 className="m-0" style={{ fontSize: '1rem', fontWeight: 700 }}>
                                    <i className="fa-solid fa-users-gear text-primary mr-1"></i> Crear Grupo de Chat
                                </h4>
                                <button className="btn-close btn-close-white" onClick={() => handleBackNavigation(() => setView('list'))}></button>
                            </div>

                            <form onSubmit={handleCreateGroupChat} className="squadflix-chat-widget__create">
                                <div className="squadflix-chat-widget__create-field">
                                    <label className="small text-muted font-weight-bold mb-1">Nombre del Grupo</label>
                                    <input 
                                        type="text" 
                                        className="squadflix-chat-widget__create-input" 
                                        placeholder="Ej. Plan de fin de semana 🍿"
                                        value={groupName}
                                        onChange={(e) => setGroupName(e.target.value)}
                                        required
                                    />
                                </div>

                                <label className="small text-muted font-weight-bold">Invitar amigos</label>
                                {loadingFriends ? (
                                    <div className="d-flex justify-content-center py-4">
                                        <Spinner />
                                    </div>
                                ) : friends.length === 0 ? (
                                    <div className="text-center py-4 text-muted small">No tienes amigos para invitar</div>
                                ) : (
                                    <div className="squadflix-chat-widget__create-friends-list">
                                        {friends.map(friend => {
                                            const isChecked = selectedFriendUids.includes(friend.uid);
                                            return (
                                                <div 
                                                    key={friend.uid} 
                                                    className="squadflix-chat-widget__create-friend-row justify-content-between"
                                                    onClick={() => {
                                                        setSelectedFriendUids(prev => 
                                                            isChecked ? prev.filter(uid => uid !== friend.uid) : [...prev, friend.uid]
                                                        );
                                                    }}
                                                >
                                                    <div className="d-flex align-items-center gap-2">
                                                        <img 
                                                            src={friend.photoURL ? getCachedImageUrl(friend.photoURL) : avatarDefault} 
                                                            alt={friend.displayName} 
                                                            className="squadflix-chat-widget__message-avatar"
                                                        />
                                                        <div>
                                                            <p className="m-0 font-weight-bold" style={{ fontSize: '0.85rem' }}>{friend.displayName}</p>
                                                            <p className="m-0 text-muted small">#{friend.userCode}</p>
                                                        </div>
                                                    </div>
                                                    <input 
                                                        type="checkbox" 
                                                        className="squadflix-chat-widget__create-checkbox"
                                                        checked={isChecked}
                                                        readOnly
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                <div className="squadflix-chat-widget__create-actions">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => handleBackNavigation(() => setView('list'))}
                                    >
                                        Cancelar
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn btn-primary btn-sm"
                                        disabled={!groupName.trim()}
                                    >
                                        Crear Grupo
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* View: Add Member to active group */}
                    {view === 'add-member' && activeRoomId && (
                        <div className="squadflix-chat-widget__overlay-modal">
                            <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                                <h4 className="m-0" style={{ fontSize: '1rem', fontWeight: 700 }}>
                                    <i className="fa-solid fa-user-plus text-primary mr-1"></i> Invitar al Grupo
                                </h4>
                                <button className="btn-close btn-close-white" onClick={() => handleBackNavigation(() => setView('chat'))}></button>
                            </div>

                            {loadingFriends ? (
                                <div className="d-flex justify-content-center py-5">
                                    <Spinner />
                                </div>
                            ) : friendsToInvite.length === 0 ? (
                                <div className="text-center py-5 text-muted small">
                                    <p>Todos tus amigos ya están en este grupo.</p>
                                </div>
                            ) : (
                                <div className="squadflix-chat-widget__create-friends-list">
                                    {friendsToInvite.map(friend => (
                                        <div 
                                            key={friend.uid} 
                                            className="squadflix-chat-widget__create-friend-row justify-content-between align-items-center"
                                        >
                                            <div className="d-flex align-items-center gap-2">
                                                <img 
                                                    src={friend.photoURL ? getCachedImageUrl(friend.photoURL) : avatarDefault} 
                                                    alt={friend.displayName} 
                                                    className="squadflix-chat-widget__message-avatar"
                                                />
                                                <div>
                                                    <p className="m-0 font-weight-bold" style={{ fontSize: '0.85rem' }}>{friend.displayName}</p>
                                                    <p className="m-0 text-muted small">#{friend.userCode}</p>
                                                </div>
                                            </div>
                                            <button 
                                                className="btn btn-primary btn-sm py-1 px-3"
                                                onClick={() => handleAddMemberToGroup(friend.uid)}
                                            >
                                                Agregar
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* View: Edit Group Details */}
                    {view === 'edit-group' && activeRoom && (
                        <div className="squadflix-chat-widget__overlay-modal">
                            <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                                <h4 className="m-0" style={{ fontSize: '1rem', fontWeight: 700 }}>
                                    <i className="fa-solid fa-pen-to-square text-primary mr-1"></i> Editar Detalles del Grupo
                                </h4>
                                <button className="btn-close btn-close-white" onClick={() => handleBackNavigation(() => setView('chat'))}></button>
                            </div>

                            <form onSubmit={handleUpdateGroupDetails} className="squadflix-chat-widget__edit-form" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    {/* Large Avatar Editor */}
                                    <div className="squadflix-chat-widget__edit-avatar-section">
                                        <div 
                                            className="squadflix-chat-widget__edit-avatar-wrapper"
                                            onClick={() => editFileInputRef.current?.click()}
                                            title="Seleccionar foto de perfil del grupo"
                                        >
                                            <img 
                                                src={editGroupFilePreview || getRoomPhotoURL(activeRoom) || avatarDefault} 
                                                alt="Group Profile" 
                                                className="squadflix-chat-widget__edit-avatar-img"
                                            />
                                            <div className="squadflix-chat-widget__edit-avatar-overlay">
                                                <i className="fa-solid fa-camera"></i>
                                                <span>CAMBIAR</span>
                                            </div>
                                        </div>
                                        <input 
                                            type="file" 
                                            style={{ display: 'none' }} 
                                            ref={editFileInputRef} 
                                            onChange={(e) => {
                                                if (e.target.files && e.target.files.length > 0) {
                                                    setEditGroupFile(e.target.files[0]);
                                                }
                                            }}
                                            accept="image/*"
                                        />
                                        {editGroupFile && (
                                            <button 
                                                type="button" 
                                                className="btn btn-sm btn-outline-danger mt-2 py-0 px-2"
                                                style={{ fontSize: '0.75rem' }}
                                                onClick={() => {
                                                    setEditGroupFile(null);
                                                    setEditGroupFilePreview(null);
                                                    if (editFileInputRef.current) editFileInputRef.current.value = "";
                                                }}
                                            >
                                                Quitar selección
                                            </button>
                                        )}
                                    </div>

                                    <div className="squadflix-chat-widget__create-field">
                                        <label className="small text-muted font-weight-bold mb-1">Nombre del Grupo</label>
                                        <input 
                                            type="text" 
                                            className="squadflix-chat-widget__create-input" 
                                            placeholder="Nombre del grupo"
                                            value={editGroupName}
                                            onChange={(e) => setEditGroupName(e.target.value)}
                                            required
                                        />
                                    </div>

                                    {/* Members list */}
                                    <div className="squadflix-chat-widget__edit-members-section">
                                        <label className="small text-muted font-weight-bold mb-2">
                                            Miembros del Grupo ({activeRoom.members.length})
                                        </label>
                                        <div className="d-flex flex-column gap-2" style={{ maxHeight: '180px', overflowY: 'auto', paddingRight: '4px' }}>
                                            {activeRoom.members.map(member => (
                                                <div 
                                                    key={member.uid} 
                                                    className="d-flex align-items-center gap-2 p-2 rounded" 
                                                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)' }}
                                                >
                                                    <img 
                                                        src={member.photoURL ? getCachedImageUrl(member.photoURL) : avatarDefault} 
                                                        alt={member.displayName} 
                                                        className="squadflix-chat-widget__message-avatar"
                                                        style={{ width: '28px', height: '28px' }}
                                                    />
                                                    <div style={{ minWidth: 0, flex: 1 }}>
                                                        <div className="d-flex align-items-center flex-wrap gap-1">
                                                            <p className="m-0 font-weight-bold text-truncate" style={{ fontSize: '0.8rem', maxWidth: '160px' }}>
                                                                {member.displayName}
                                                            </p>
                                                            {member.uid === currentUserId && (
                                                                <span className="badge bg-primary text-white" style={{ fontSize: '0.6rem', padding: '1px 4px', lineHeight: 1 }}>Tú</span>
                                                            )}
                                                            {member.uid === activeRoom.created_by && (
                                                                <span className="badge bg-warning text-dark d-inline-flex align-items-center" style={{ fontSize: '0.6rem', padding: '1px 4px', fontWeight: 'bold', gap: '2px', lineHeight: 1 }}>
                                                                    <i className="fa-solid fa-crown" style={{ fontSize: '0.55rem' }}></i>
                                                                    Creador
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="m-0 text-muted small text-truncate" style={{ fontSize: '0.7rem' }}>#{member.userCode}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="squadflix-chat-widget__create-actions mt-3 border-top pt-2">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => handleBackNavigation(() => setView('chat'))}
                                        disabled={updatingGroupDetails}
                                    >
                                        Cancelar
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn btn-primary btn-sm"
                                        disabled={updatingGroupDetails || (!editGroupName.trim() && !editGroupFile)}
                                    >
                                        {updatingGroupDetails ? <Spinner className="spinner-sm" /> : "Guardar Cambios"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* View: User Profile Summary */}
                    {view === 'user-profile' && selectedProfileUser && (
                        <div className="squadflix-chat-widget__overlay-modal justify-content-center align-items-center">
                            <div className="squadflix-chat-widget__profile-card">
                                <button 
                                    className="squadflix-chat-widget__profile-close"
                                    onClick={() => handleBackNavigation(() => setView('chat'))}
                                    title="Volver"
                                >
                                    <i className="fa-solid fa-xmark"></i>
                                </button>
                                
                                <div 
                                    className="squadflix-chat-widget__profile-banner"
                                    style={{
                                        backgroundImage: detailedUserProfile?.preferences?.profileBackground 
                                            ? `url(${getTmdbImage(detailedUserProfile.preferences.profileBackground)})` 
                                            : 'none',
                                        backgroundColor: detailedUserProfile?.preferences?.profileBackground ? 'transparent' : 'rgba(255, 255, 255, 0.05)'
                                    }}
                                />

                                <div className="squadflix-chat-widget__profile-avatar-container">
                                    <img 
                                        src={selectedProfileUser.photoURL ? getCachedImageUrl(selectedProfileUser.photoURL) : avatarDefault} 
                                        alt={selectedProfileUser.displayName} 
                                        className="squadflix-chat-widget__profile-avatar"
                                    />
                                </div>

                                <h3 className="squadflix-chat-widget__profile-name">{selectedProfileUser.displayName}</h3>
                                <p className="squadflix-chat-widget__profile-code">#{selectedProfileUser.userCode}</p>



                                <div className="squadflix-chat-widget__profile-actions px-3 mt-3">
                                    <button 
                                        className="btn btn-primary w-100"
                                        onClick={() => handleNavigateToProfile(selectedProfileUser)}
                                    >
                                        <i className="fa-solid fa-user mr-1" style={{ marginRight: '6px' }}></i> Ver Perfil Completo
                                    </button>
                                    <button 
                                        className="btn btn-secondary w-100 mt-2"
                                        onClick={() => handleBackNavigation(() => setView('chat'))}
                                    >
                                        Volver al Chat
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            )}

            {/* View: Image / Media Lightbox Preview */}
            {isOpen && previewAttachmentUrl && (
                <div className="squadflix-chat-widget__lightbox" onClick={() => handleBackNavigation(() => setPreviewAttachmentUrl(null))}>
                    {/* Close Button */}
                    <button 
                        className="squadflix-chat-widget__lightbox-close"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleBackNavigation(() => setPreviewAttachmentUrl(null));
                        }}
                        title="Cerrar"
                    >
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                    
                    {/* Download Button */}
                    {previewAttachmentId && (
                        <button 
                            className="squadflix-chat-widget__lightbox-download"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadAttachment(previewAttachmentId, previewAttachmentName);
                            }}
                            title="Descargar"
                        >
                            <i className="fa-solid fa-download"></i>
                        </button>
                    )}

                    <div className="squadflix-chat-widget__lightbox-content" onClick={(e) => e.stopPropagation()}>
                        {previewAttachmentMime.startsWith("image/") && (
                            <img 
                                src={previewAttachmentUrl} 
                                alt={previewAttachmentName} 
                                className="squadflix-chat-widget__lightbox-img"
                            />
                        )}
                        {previewAttachmentMime.startsWith("video/") && (
                            <video 
                                controls 
                                autoPlay 
                                src={previewAttachmentUrl} 
                                className="squadflix-chat-widget__lightbox-video" 
                            />
                        )}
                        {previewAttachmentMime.startsWith("audio/") && (
                            <audio 
                                controls 
                                autoPlay 
                                src={previewAttachmentUrl} 
                                className="squadflix-chat-widget__lightbox-audio" 
                            />
                        )}
                        {previewAttachmentMime === "application/pdf" && (
                            <iframe 
                                src={previewAttachmentUrl} 
                                title={previewAttachmentName} 
                                className="squadflix-chat-widget__lightbox-pdf" 
                            />
                        )}
                        <div className="squadflix-chat-widget__lightbox-caption">
                            {previewAttachmentName}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SquadflixChatWidget;
